import React from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Instagram, Search, Phone, MessageSquare } from "lucide-react";
import { useContent } from "@/lib/contentStore";
import ContactDialog from "@/components/ContactDialog";
import { COMPANY_INFO } from "@/lib/companyInfo";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function Header() {
  const { content } = useContent();
  const t = content.uiTexts || {};
  const logoUrl = content.brand.logoUrl?.trim();
  const [logoFailed, setLogoFailed] = React.useState(false);

  React.useEffect(() => {
    setLogoFailed(false);
  }, [logoUrl]);

  return (
    <header className="sticky top-0 z-40 border-b border-[#e7e4df] bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/88">
      <div className="mx-auto flex max-w-6xl items-center gap-3 px-3 py-3 sm:gap-6 sm:px-4 sm:py-4">
        <Link to="/" className="flex items-center gap-3">
          <div className="flex h-9 w-[132px] items-center overflow-hidden sm:w-[148px]">
            {logoUrl && !logoFailed ? (
              <img
                src={logoUrl}
                alt={content.brand.name}
                className="h-9 w-[178px] object-cover object-center sm:w-[190px]"
                style={{ transform: "translateX(-10px) scale(1.08)" }}
                onError={() => setLogoFailed(true)}
                loading="eager"
                decoding="async"
              />
            ) : (
              <span className="text-[14px] font-black uppercase tracking-[0.14em] text-[#3f3a36]">
                DA LINI
              </span>
            )}
          </div>
        </Link>

        <nav className="hidden flex-1 items-center justify-center gap-4 text-[12px] uppercase tracking-[0.14em] text-zinc-800 lg:flex xl:gap-6 xl:text-[13px]">
          {content.nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  "rounded-full px-3 py-2.5 font-semibold transition-all xl:px-4 xl:py-3",
                  isActive
                    ? "bg-[#1f6a54] text-white shadow-[0_8px_18px_rgba(31,106,84,0.28)]"
                    : "text-[#403a34] hover:bg-[#ece3d4] hover:text-[#1f1a17]"
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2 sm:gap-4">
          <a
            href={`tel:${(content.contacts.phone || "").replace(/\s|\(|\)/g, "")}`}
            className="hidden items-center gap-2 text-sm text-zinc-800 hover:text-zinc-950 xl:flex"
          >
            <Phone className="h-4 w-4" />
            <span className="font-medium">{content.contacts.phone}</span>
          </a>
          <a
            href="https://instagram.com/dalini.by"
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#e4e1dc] bg-white text-[#4b433c] transition-colors hover:bg-[#f4f4f4] sm:h-9 sm:w-9"
            aria-label="Instagram"
          >
            <Instagram className="h-4 w-4" />
          </a>
          <button
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#e4e1dc] bg-white text-[#4b433c] transition-colors hover:bg-[#f4f4f4] sm:h-9 sm:w-9"
            aria-label={t.searchButtonAria || "Поиск"}
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent("dalini:search"))}
          >
            <Search className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      <div className="border-t border-[#ece9e4] bg-white lg:hidden">
        <div className="mx-auto max-w-6xl px-3 py-2 sm:px-4">
          <nav className="flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {content.nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  "shrink-0 whitespace-nowrap rounded-full px-3 py-2 text-xs font-semibold uppercase tracking-[0.1em] transition-colors",
                  isActive ? "bg-[#1f6a54] text-white" : "bg-[#f6f2ea] text-[#4e4740] hover:bg-[#ece3d4]"
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
          </nav>
        </div>
      </div>
    </header>
  );
}

function Footer({ onContactClick }) {
  const { content } = useContent();
  const t = content.uiTexts || {};
  const company = content.company || COMPANY_INFO;

  return (
    <footer className="border-t border-[#24201d] bg-[#171412] text-zinc-100">
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 px-3 py-8 sm:px-4 sm:py-10 md:grid-cols-3">
        <div>
          <div className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-amber-100/70">
            {content.brand.name}
          </div>
          <div className="text-sm text-zinc-300">
            {content.contacts.address}
          </div>
          <div className="mt-2 text-xs text-zinc-400">
            УНП {company.unp || COMPANY_INFO.unp}
          </div>
        </div>

        <div>
          <div className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-amber-100/70">
            {t.footerContactsTitle || "Контакты"}
          </div>
          <div className="space-y-1 text-sm text-zinc-300">
            <div>{content.contacts.phone}</div>
            <div className="text-zinc-400">{content.contacts.hours}</div>
          </div>
        </div>

        <div>
          <div className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-amber-100/70">
            {t.footerMessageTitle || "Сообщение"}
          </div>
          <div className="text-sm text-zinc-300">
            {t.footerMessageBody || "Менеджеры компании с радостью ответят на ваши вопросы."}
          </div>
        </div>
      </div>

      {/* Chat-like CTA (visual parity with Jivo label) */}
      <div className="fixed bottom-3 right-3 z-40 sm:bottom-4 sm:right-4">
        <button
          type="button"
          onClick={onContactClick}
          className="group inline-flex max-w-[calc(100vw-1.5rem)] items-center gap-2 rounded-full bg-gradient-to-r from-[#1f6a54] to-[#285f75] px-3 py-2.5 text-sm font-semibold text-white shadow-[0_14px_30px_rgba(26,52,44,0.35)] transition-all hover:translate-y-[-1px] hover:brightness-105 sm:max-w-none sm:px-4 sm:py-3"
        >
          <MessageSquare className="h-4 w-4 shrink-0" />
          <span className="sr-only">{t.contactCtaLabel || "Отправьте нам сообщение"}</span>
          <span className="hidden sm:inline md:hidden">Написать</span>
          <span className="hidden md:inline">{t.contactCtaLabel || "Отправьте нам сообщение"}</span>
        </button>
      </div>
    </footer>
  );
}

export default function SiteLayout() {
  const { pathname } = useLocation();
  const [contactOpen, setContactOpen] = React.useState(false);

  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);

  React.useEffect(() => {
    const onContact = () => setContactOpen(true);
    window.addEventListener("dalini:contact", onContact);
    return () => window.removeEventListener("dalini:contact", onContact);
  }, []);

  return (
    <div className="min-h-screen bg-transparent text-[#1f1a17]">
      <Header />
      <main className="mx-auto w-full max-w-6xl px-3 py-6 pb-24 sm:px-4 sm:py-8 sm:pb-10 lg:px-6">
        <Outlet />
      </main>
      <Footer onContactClick={() => setContactOpen(true)} />
      <ContactDialog open={contactOpen} onOpenChange={setContactOpen} source="footer_floating_cta" />
    </div>
  );
}
